import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';

import slice from './navigation.slice';

import activeEditor from 'domains/active_editor';
import catalogs from 'domains/catalogs';
import products from 'domains/products';
import subcatalogs from 'domains/subcatalogs';

import { HEIGHT_PARAM, WIDTH_PARAM } from 'utils/globals';
import { serializeOptionQueryParam } from 'utils/option_util';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectShowCatalogSelector = (state: LocalState) => state.routes.catalogSelector;
const selectShowCart = (state: LocalState) => state.routes.cart;
const selectShowEditor = (state: LocalState) => state.routes.editor;
const selectShowPriceTool = (state: LocalState) => state.routes.priceTool;
const selectShowProjects = (state: LocalState) => state.routes.projects;
const selectWasEditorPopstate = (state: LocalState) => state.wasEditorPopstate;
const selectPathHistory = (state: LocalState) => state.configuredPathHistory;

const selectCurrentRoute = (state: LocalState) => {
  const active = Object.entries(state.routes).find(([, value]) => !!value);
  return active ? active[0] : null;
};

const selectCanvasConfigPath = createSelector(
  [
    activeEditor.selectors.canvas,
    catalogs.selectors.selectEntities,
    subcatalogs.selectors.selectEntities,
    products.selectors.selectEntities,
  ],
  (activeCanvas, catalogsById, subcatalogsById, productsById) => {
    if (
      activeCanvas &&
      !activeCanvas.cartProductId &&
      activeCanvas.catalog &&
      activeCanvas.subcatalog &&
      activeCanvas.product
    ) {
      const { catalog: c, subcatalog: s, product: p } = activeCanvas;

      const catalog = catalogsById[c];
      const subcatalog = subcatalogsById[s];
      const product = productsById[p];

      return catalog && subcatalog && product
        ? `${catalog.slug}/${subcatalog.slug}/${product.slug}`
        : '';
    }

    return '';
  }
);

// Using dashes and underscores since they are not escaped in the url
// Each pair as optionGroupId_optionId
const selectCanvasOptionsQueryParam = createSelector(
  [activeEditor.selectors.canvas, activeEditor.selectors.optionConfig],
  (activeCanvas, activeOptions) => {
    if (activeCanvas && activeCanvas.cartProductId) return '';

    return serializeOptionQueryParam(activeOptions);
  }
);

const selectCustomSizeQueryParam = createSelector(
  [activeEditor.selectors.canvas, products.selectors.selectEntities],
  (activeCanvas, productsById) => {
    if (!activeCanvas) return '';

    const { product: productId, height, width } = activeCanvas;
    if (!productId || !height || !width) return '';

    const product = productsById[productId];
    if (!product) return '';

    const isCustom = products.utils.isCustomSizeProduct(product);
    if (!isCustom) return '';

    return `${HEIGHT_PARAM}=${height}&${WIDTH_PARAM}=${width}`;
  }
);

export default {
  showCatalogSelector: compose(selectShowCatalogSelector, localState),
  showCart: compose(selectShowCart, localState),
  showEditor: compose(selectShowEditor, localState),
  showPriceTool: compose(selectShowPriceTool, localState),
  showProjects: compose(selectShowProjects, localState),
  wasEditorPopstate: compose(selectWasEditorPopstate, localState),
  history: compose(selectPathHistory, localState),
  current: compose(selectCurrentRoute, localState),
  // Globalized
  canvasConfigPath: selectCanvasConfigPath,
  optionsQueryParam: selectCanvasOptionsQueryParam,
  customSizeQueryParam: selectCustomSizeQueryParam,
};
